/**
 * Pause queue = create a holiday (closure) for this queue on a date / range —
 * mirrors the mobile PauseQueueModal. Resume = delete that holiday (handled by
 * the parent). Warns (check-tokens) when booked tokens fall in the range.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
  ToggleButtonGroup, ToggleButton, Alert, CircularProgress,
} from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';
import { useHolidays } from '../../hooks/useHolidays';
import { CheckTokensResult } from '../../api/holidayTypes';

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface Props {
  queueId: string;
  queueName: string;
  defaultDate: Date;
  onClose: () => void;
  onPaused: (msg: string) => void;
}

const PauseQueueDialog: React.FC<Props> = ({ queueId, queueName, defaultDate, onClose, onPaused }) => {
  const { checkTokens, createHoliday } = useHolidays();
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [start, setStart] = useState(ymd(defaultDate));
  const [end, setEnd] = useState(ymd(defaultDate));
  const [reason, setReason] = useState('Queue paused');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<CheckTokensResult | null>(null);

  const params = () => ({ queue_ids: [queueId], start_date: start, end_date: mode === 'range' ? end : start, reason: reason.trim() || 'Queue paused' });

  const submit = async (force: boolean) => {
    if (mode === 'range' && end < start) { setErr('End date must be on or after start date.'); return; }
    setBusy(true); setErr(null);
    try {
      if (!force) {
        const check = await checkTokens(params());
        if (check.has_tokens && check.total_count > 0) { setWarn(check); setBusy(false); return; }
      }
      const res = await createHoliday(params());
      onPaused(res.total_cancelled_tokens > 0
        ? `Queue paused — ${res.total_cancelled_tokens} token(s) cancelled, customers notified`
        : 'Queue paused');
    } catch (e: any) { setErr(e?.response?.data?.message || 'Failed to pause queue.'); setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Pause “{queueName}”</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Pausing closes the queue for the chosen day(s); customers can't book and existing tokens are cancelled. Resume any time from the queue menu.
        </Typography>

        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)} sx={{ mb: 2 }}>
          <ToggleButton value="single">Single day</ToggleButton>
          <ToggleButton value="range">Date range</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField type="date" label="Start date" InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} fullWidth />
          {mode === 'range' && <TextField type="date" label="End date" InputLabelProps={{ shrink: true }} value={end} onChange={(e) => setEnd(e.target.value)} fullWidth />}
        </Box>

        <TextField fullWidth label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />

        {warn && (
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mt: 2 }}>
            <b>{warn.total_count} booked token(s)</b> fall in this range and will be cancelled. Affected customers should be notified. Proceed?
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {warn ? (
          <Button variant="contained" color="warning" disabled={busy} onClick={() => submit(true)}>{busy ? <CircularProgress size={20} color="inherit" /> : 'Proceed anyway'}</Button>
        ) : (
          <Button variant="contained" color="error" disabled={busy} onClick={() => submit(false)}>{busy ? <CircularProgress size={20} color="inherit" /> : 'Pause queue'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PauseQueueDialog;
